import { Site } from '@type';
import { parseSite } from '@utils';

import { initialState, STORE_NAME } from './site-store.constant';
import {
  AddSite,
  DeleteSiteByUUID,
  GetSiteByProjectId,
  GetSiteByUUID,
  GetType,
  IsSiteExist,
  ResetSites,
  SetType,
  UpdateSite,
} from './site-store.types';

export const addSite = (set: SetType, get: GetType): AddSite => {
  return (site, projectId) => {
    if (site.siteUuid && get().isSiteExist(site.siteUuid)) {
      return;
    }

    const newSite: Site = parseSite(site, projectId);
    set(
      {
        sites: [...get().sites, { ...newSite }],
      },
      false,
      `${STORE_NAME}-ADD_SITE`
    );
  };
};

export const updateSite = (set: SetType, get: GetType): UpdateSite => {
  return site => {
    if (!site.siteUuid) {
      throw new Error('siteUUID is required!');
    }
    if (!get().isSiteExist(site.siteUuid)) {
      throw new Error('Site does not exist!');
    }

    set(
      {
        sites: [
          ...get().sites.map(item =>
            item.siteUuid === site.siteUuid ? { ...item, ...site } : item
          ),
        ],
      },
      false,
      `${STORE_NAME}-UPDATE_SITE`
    );
  };
};

export const deleteSiteByUUID = (
  set: SetType,
  get: GetType
): DeleteSiteByUUID => {
  return (siteUuid: string) => {
    if (!siteUuid) {
      throw new Error('siteUUID is required!');
    }
    if (!get().isSiteExist(siteUuid)) {
      throw new Error('Site does not exist!');
    }

    const newSiteList = get().sites.filter(item => item.siteUuid !== siteUuid);
    set(
      {
        sites: newSiteList,
      },
      false,
      `${STORE_NAME}-DELETE_SITE`
    );

    return newSiteList;
  };
};

export const getSiteByUUID = (get: GetType): GetSiteByUUID => {
  return siteUuid => {
    const site = get().sites.find(item => item.siteUuid === siteUuid);
    if (!site) {
      throw new Error(`The site does not exist. siteUuid: ${siteUuid}`);
    }
    return site;
  };
};

export const getSiteByProjectId = (get: GetType): GetSiteByProjectId => {
  return projectId => {
    const site = get().sites.find(item => item.sourceProjectId === projectId);
    if (!site) {
      throw new Error(`The site does not exist. projectId: ${projectId}`);
    }
    return site;
  };
};

export const isSiteExist = (get: GetType): IsSiteExist => {
  return siteUuid => get().sites.some(item => item.siteUuid === siteUuid);
};

export const resetSites = (set: SetType): ResetSites => {
  return () => set(initialState, false, `${STORE_NAME}-RESET_SITES`);
};

export const removeUnauthorizedSites = (set: SetType, get: GetType) => () =>
  set(
    {
      sites: get().sites.filter(site => site.sharedSecretKey),
    },
    false,
    `${STORE_NAME}-REMOVE_UNCONSENTED_SITES`
  );
